import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { useWidgetsModalContext } from '../../../shared/providers/WidgetsModalProvider';
import useOrganisationsList from '../../../utilities/hooks/queryHooks/useOrganizationsList';
import useTracking from '../../../utilities/hooks/useTracking';
import RadioGroup from '../../widgets/RadioGroup';

import {
  DateFilterOptions,
  SpecialDateFilterValue,
} from '../../../shared/constants/HeadingFilter';
import { IDateRange } from '../../../shared/types/filters';
import { useDeviceType } from '../../../utilities/common/deviceType';
import {
  ExportType,
  PageType,
  ReportStatus,
  ReportTypes,
} from '../../../shared/constants/Rerpots';

import Modal from '../../widgets/Modal';
import OrgSelection from './OrgSelection';
import Button from '../../widgets/Button';
import {
  useReportsData,
  useReportsDownloadData,
  useReportsGenerateData,
} from '../../../utilities/hooks/queryHooks/useReportsData';
import BottomSheet from '../Layout/Header/BottomSheet/BottomSheet';
import Toaster from '../../widgets/Toast';
import Reportgenerated from './Modal/ReportGenerated';
import ReportFailed from './Modal/ReportFailed';
import PastReports from './PastReports';
import {
  EventActions,
  EventCategories,
  EventNames,
} from '../../../shared/constants/analytics';
import {
  formatDateToDisplayLabel,
  getDateFilterWindow,
} from '../../../utilities/common/date';
import { format } from 'date-fns';
import { ISO_DATE_FORMAT } from '../../../shared/constants/date';
import useAuth from '../../../utilities/hooks/useAuth';
import { useFilterData } from '../../../utilities/hooks/useFilterData';
import { Contact } from '../../../shared/constants/contact';
import toastError from '../../../assets/icons/toast-error.svg';
import Close from '../../../assets/icons/General/close.svg?react';
import { Typography } from '../../widgets/Typography';
import DateRangePickerModal from '../Filters/DateFilter/DateRangePickerModal';

const radioGroupClassNames = {
  parent: 'grid grid-cols-3 gap-x-2 gap-y-4',
  option:
    'p-4 rounded-[1.125rem] border border-grey-100 hover:border-grey-100 cursor-pointer data-[selected=true]:border-blue-700 data-[selected=true]:bg-blue-25',
};

const DownloadReport = () => {
  const { loadingSelf } = useAuth();

  const [selectedReportType] = useState(ReportTypes[0]);

  const [selectedItems, setSelectedItems] = useState<
    { value: string; id: any }[]
  >([]);

  const [reportFail, setReportFail] = useState(false);
  const [generatedSuccessfully, setGeneratedSuccessfully] = useState(false);
  const [downloadReportId, setDownloadReportId] = useState<null | string>(null);
  const [downloading, setDownloading] = useState(false);
  const [preGenerated, setPreGenerated] = useState(false);

  const [isLoadingGenerateReport, setIsLoadingGenerateReport] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [pollIterval, setPollInterval] = useState(false);

  const [showDatePicker, setShowDatePicker] = useState(false);

  const [modalReportDateRange, setModalReportDateRange] = useState<IDateRange>(
    () => {
      const today = format(new Date(), ISO_DATE_FORMAT);
      return { fromDate: today, toDate: today };
    },
  );

  const [selected, setSelected] = useState(DateFilterOptions[0]);

  const { showDownloadReport, setShowDownloadReport, showOrgFilter } =
    useWidgetsModalContext();

  const isMobile = useDeviceType();
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const { configData } = useFilterData();

  const availableFilters = DateFilterOptions.filter(
    (filter) =>
      filter.value === SpecialDateFilterValue.ALL_TIME ||
      !filter.isOrgDim2Filter,
  );

  const { data: orgList, refetch: refetchOrgList } = useOrganisationsList(
    showOrgFilter,
    {
      enable: false,
      onSuccess: () => {},
      onError: () => {},
    },
  );

  const {
    data: pastReportsData,
    refetch: refetchPastReports,
    isLoading: isLoadingPastReports,
  } = useReportsData({
    enable: false,
    onSuccess: (succ) => {
      if (succ[0]?.status === ReportStatus.PENDING) {
        if (!pollIterval) {
          // Only update if pollInterval is not already true
          setDownloadReportId(succ[0].id);
          setPollInterval(true);
          setFormDisabled(true);
        }
      }
      if (
        succ[0]?.status === ReportStatus.FAILED ||
        succ[0]?.status === ReportStatus.DONE
      ) {
        if (pollIterval) {
          // Only update if pollInterval is true
          setPollInterval(false);
          setFormDisabled(false);
        }
      }
    },
    onError: () => {},
  });

  const isReportLoading =
    pastReportsData &&
    pastReportsData.length > 0 &&
    pastReportsData[0].status === String(ReportStatus.PENDING);

  // Set insights report type to new name for api
  let newReportType = selectedReportType;
  if (selectedReportType === 'INSIGHTS_REPORT') {
    newReportType = 'cs_wmd_report';
  }

  React.useEffect(() => {
    setModalReportDateRange((prevModalReportDateRange) => {
      if (configData?.contractStartDate) {
        const newDateRange = getDateFilterWindow({
          monthOffset: SpecialDateFilterValue.ALL_TIME,
          contractDate: configData.contractStartDate,
        });
        return newDateRange; // Return the updated date range
      }

      return prevModalReportDateRange; // Maintain previous value if no contractStartDate
    });
  }, [configData]);

  let generateReportArgs = {
    fromDate: modalReportDateRange.fromDate + ' 00:00:00',
    toDate: modalReportDateRange.toDate + ' 23:59:59',
    reportType: newReportType,
    fileType: ExportType[0]?.value,
  };

  const [organisationIds, setOrganisationIds] = useState<{
    organisationId: number[];
  }>({ organisationId: [] });

  useEffect(() => {
    const ids = selectedItems.map((item) => item.id);
    const result = { organisationId: ids };
    setOrganisationIds(result);
  }, [selectedItems]);

  const { refetch: refetchGenerateReport } = useReportsGenerateData(
    generateReportArgs,
    organisationIds,
    {
      enable: false,
      onSuccess: (data) => {
        if (data) {
          refetchPastReports();
          setDownloadReportId(data);
          setTimeout(() => {
            refetchDownloadReport();
          }, 100);
          setIsLoadingGenerateReport(false);
          setPollInterval(true);
        }
      },
      onError: () => {
        setReportFail(true);
        setFormDisabled(false);
      },
    },
  );

  // Download report
  const { refetch: refetchDownloadReport } = useReportsDownloadData(
    downloadReportId,
    {
      enable: false,
      onSuccess: (data) => {
        if (
          data?.data?.status == ReportStatus.DONE &&
          data?.data?.url != null &&
          data?.data?.url
        ) {
          setDownloadReportId(null);
          setPollInterval(false);
          !preGenerated && setGeneratedSuccessfully(true);
          const url = data?.data?.url;
          const link = document.createElement('a');
          link.href = url;
          link.click();

          setTimeout(() => {
            link.remove();
            URL.revokeObjectURL(link.href);
          }, 700);

          setDownloading(false);
          // setPreGenerated(false);
        }

        if (data?.data?.status == ReportStatus.DONE) {
          setFormDisabled(false);
          refetchPastReports();
        } else if (data?.data?.status == ReportStatus.FAILED) {
          setReportFail(true);
          refetchPastReports();
        }
      },
      onError: () => {
        setFormDisabled(false);
      },
    },
  );

  const generateOptions = () => {
    return (
      orgList?.map((item: any) => ({
        value: item.name,
        label: item.name,
        id: item.id,
      })) || []
    );
  };

  const handleSubmit = async () => {
    setPreGenerated(false);
    if (configData?.isTrial) {
      setShowDownloadReport(false);
      toast(
        <div>
          <div className="border-redd-300 bg-redd-25 m-2 rounded-xl border shadow-[0px_1px_2px_0px_#1018280D] lg:m-0">
            <div className="mb-2 flex justify-between pr-[21px]">
              <img src={toastError} className="p-[7px]" alt="" />
              <button className="text-sm font-medium">
                <Close
                  stroke="var(--color-black-500)"
                  onClick={() => toast.dismiss('trial')}
                  className="w-[10px]"
                />
              </button>
            </div>
            <div className="px-4 pb-4 text-left">
              <p className="text-black-600 mb-3 text-sm">
                Sorry, not enough data for a report download.
              </p>
              <p className="text-black-600 text-sm">
                <label>Contact us at</label>{' '}
                <a
                  href={`mailto:${Contact.Email}`}
                  className="text-primary-600 font-semibold"
                >
                  {Contact.Email}
                </a>{' '}
                for any questions
              </p>
            </div>
          </div>
        </div>,
        {
          toastId: 'trial',
          autoClose: 5000,
        },
      );
      return;
    }
    setFormDisabled(true);
    setIsLoadingGenerateReport(true);
    refetchGenerateReport();

    trackEvent(EventNames.WellBeingDashboard, {
      eventAction: EventActions.click,
      eventCategory: EventCategories.GenerateReport,
      eventLabel: 'generate_report',
    });
  };

  const handleSubmitDownload = (id: string) => {
    setPreGenerated(true);
    setDownloadReportId(id);
    setDownloading(true);
    setFormDisabled(true);
    setTimeout(() => {
      refetchDownloadReport();
    }, 100);

    trackEvent(EventNames.WellBeingDashboard, {
      eventAction: EventActions.click,
      eventCategory: EventCategories.GenerateReport,
      downloadReport: 'download_report',
    });
  };

  const canSelectOrganisations = !!configData?.showOrganisationDropdown;

  useEffect(() => {
    refetchPastReports();
  }, []);

  useEffect(() => {
    if (!loadingSelf && canSelectOrganisations) refetchOrgList();
  }, [loadingSelf, canSelectOrganisations]);

  useEffect(() => {
    let intervalId: any;
    if (pollIterval) {
      intervalId = setInterval(() => {
        if (showDownloadReport) {
          refetchDownloadReport();
        }
      }, 5000);
    }
    return () => clearInterval(intervalId);
  }, [pollIterval, showDownloadReport]);

  const getFilterLabel = (
    filter: (typeof DateFilterOptions)[number],
  ): string => {
    // Custom date range - show formatted date range
    if (
      selected.value === SpecialDateFilterValue.CUSTOM_DATE &&
      filter.value === SpecialDateFilterValue.CUSTOM_DATE
    ) {
      return `${formatDateToDisplayLabel(modalReportDateRange.fromDate)} - ${formatDateToDisplayLabel(modalReportDateRange.toDate)}`;
    }

    // ALL_TIME filter - just return the label
    if (filter.value === SpecialDateFilterValue.ALL_TIME) {
      return t(filter.label);
    }

    // CUSTOM_DATE filter (not selected) - just return the label
    if (filter.value === SpecialDateFilterValue.CUSTOM_DATE) {
      return t(filter.label);
    }

    // Regular month filters - add "LAST" prefix and "MONTHS" suffix
    return `${t('LAST')} ${t(filter.label)} ${t('MONTHS')}`;
  };

  const onDateRadioGroupChange = (value: number) => {
    // Always find and set the selected filter first for visual feedback
    const selectedFilter = DateFilterOptions.find(
      (filter) => filter.value === value,
    );
    if (!selectedFilter) return;
    setSelected(selectedFilter);

    // Open date picker for custom date selection. Date selection never affects
    // an in-flight report — generation is gated at the Download button.
    if (value === SpecialDateFilterValue.CUSTOM_DATE) {
      setShowDatePicker(true);
      return;
    }

    const newDialogDateRange = getDateFilterWindow({
      monthOffset: selectedFilter.value,
      contractDate: configData?.contractStartDate,
    }) as IDateRange;
    setModalReportDateRange(newDialogDateRange);
  };

  const reportsElement = () => (
    <div
      className={`${
        !isMobile
          ? 'divide-black-200 flex divide-x'
          : 'divide-black-200 mb-5 block max-h-[94vh] gap-y-8 divide-y overflow-auto px-6 py-[70px] pt-[147px] pb-2'
      }`}
    >
      <div className={'w-full md:max-w-[641px] md:min-w-[641px] md:p-6'}>
        <div
          className={`${
            isMobile &&
            'rounded- absolute top-0 left-0 z-20 w-full bg-white pt-[30px] pb-6 pl-6'
          }`}
        >
          <Typography as="h3" variant="body-xl" weight="bold" className="mb-2">
            {t('DOWNLOAD_REPORTS')}
          </Typography>
          <Typography
            variant="body-sm"
            className="text-grey-700 mb-6 text-balance"
          >
            {t('DOWNLOAD_REPORTS_SUBHEADING')}
          </Typography>
        </div>
        {!loadingSelf && canSelectOrganisations && (
          <div className="mb-6">
            <Typography
              variant="body-sm"
              weight="medium"
              className="text-grey-800 mb-4 text-sm"
            >
              {t('organisation')}
            </Typography>
            <OrgSelection
              options={generateOptions()}
              placeholder={t('SELECT') + ' ' + t('organisation')}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          </div>
        )}
        <div className="mb-6">
          <Typography
            variant="body-sm"
            weight="medium"
            className="text-grey-800 mb-4"
          >
            Date
          </Typography>
          <RadioGroup
            value={selected.value}
            onChange={(value) => onDateRadioGroupChange(value)}
            options={availableFilters.map((filter) => ({
              label: getFilterLabel(filter),
              value: filter.value,
            }))}
            name="report-date"
            direction="column"
            className={radioGroupClassNames.parent}
            optionsClassName={radioGroupClassNames.option}
          />
        </div>
        <div className="mb-6">
          <Typography
            variant="body-sm"
            weight="medium"
            className="text-grey-800 mb-4"
          >
            {t('PAGE')}
          </Typography>{' '}
          <RadioGroup
            value={PageType[0].value}
            onChange={() => {}}
            options={PageType.map((type) => ({
              label: t(type.name),
              value: type.value,
            }))}
            name="page-type"
            direction="column"
            className={radioGroupClassNames.parent}
            optionsClassName={radioGroupClassNames.option}
          />
        </div>

        <div className="mb-6">
          <Typography
            variant="body-sm"
            weight="medium"
            className="text-grey-800 mb-4"
          >
            {t('DOWNLOAD_AS')}
          </Typography>
          <RadioGroup
            value={ExportType[0].value}
            onChange={() => {}}
            options={ExportType.map((type) => ({
              label: type.name,
              value: type.value,
            }))}
            name="export-type"
            direction="column"
            className={radioGroupClassNames.parent}
            optionsClassName={radioGroupClassNames.option}
          />
        </div>
        <div className="w-full">
          <Button
            size="lg"
            className="w-full"
            isLoading={isLoadingGenerateReport || isReportLoading}
            disabled={
              !ExportType ||
              !modalReportDateRange ||
              !selectedReportType ||
              formDisabled
            }
            onClick={handleSubmit}
          >
            {t('DOWNLOAD')}
          </Button>
        </div>
      </div>

      <PastReports
        pastReportsData={pastReportsData}
        isLoadingPastReports={isLoadingPastReports}
        handleSubmitDownload={handleSubmitDownload}
        formDisabled={formDisabled}
        canSelectOrganisations={canSelectOrganisations}
      />
    </div>
  );

  const handleHide = () => setShowDownloadReport(false);

  useEffect(() => {
    if (reportFail || generatedSuccessfully) setShowDownloadReport(false);
  }, [reportFail, generatedSuccessfully]);

  return (
    <div>
      {/*Download Report Modal Desktop and Mobile*/}
      <Modal
        open={!isMobile && showDownloadReport}
        handleClose={() => !showDatePicker && setShowDownloadReport(false)}
        closeButton
        modalClassName="!p-0"
      >
        {reportsElement()}
      </Modal>
      {isMobile && pastReportsData && (
        <BottomSheet show={showDownloadReport} onHide={handleHide}>
          {reportsElement()}
        </BottomSheet>
      )}

      {/* Report status popup and toast*/}
      <ReportFailed
        open={!preGenerated && reportFail}
        setReportfail={(e) => setReportFail(e)}
      />
      <Reportgenerated
        open={!preGenerated && generatedSuccessfully}
        setGeneratedSuccessfully={(e) => {
          setGeneratedSuccessfully(e);
        }}
      />
      <Toaster />

      <DateRangePickerModal
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        dateRange={modalReportDateRange}
        onApply={(appliedDateRange) => {
          setModalReportDateRange(appliedDateRange);
          setShowDatePicker(false);
        }}
      />
    </div>
  );
};

export default DownloadReport;
